import { lazy, Suspense } from 'react';
import { ActivatePage } from '../features/auth/ActivatePage';
import { LoginPage } from '../features/auth/LoginPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { DocumentsPage } from '../features/documents/DocumentsPage';
import { LeavePage } from '../features/leave/LeavePage';
import { NotificationsPage } from '../features/notifications/NotificationsPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { CompaniesPage } from '../features/superadmin/CompaniesPage';
import { InfrastructurePage } from '../features/superadmin/InfrastructurePage';
import { ModulesPage } from '../features/superadmin/ModulesPage';
import { OverviewPage } from '../features/superadmin/OverviewPage';
import { SuperadminLoginPage } from '../features/superadmin/SuperadminLoginPage';
import { SuperadminShell } from '../features/superadmin/SuperadminShell';
import { SystemPage } from '../features/superadmin/SystemPage';
import { TasksPage } from '../features/tasks/TasksPage';
import { UsersPage } from '../features/users/UsersPage';
import { PartnersPage } from '../features/partners/PartnersPage';
import { WorkOrdersPage } from '../features/work-orders/WorkOrdersPage';
import { ShiftsPage } from '../features/shifts/ShiftsPage';
import { TenantShell } from '../features/app/TenantShell';
import { readToken, superadminTokenKey, tenantTokenKey } from './session';
import { Navigate, useLocation } from './router';

const AttendancePage = lazy(() =>
  import('../features/attendance/AttendancePage').then((module) => ({ default: module.AttendancePage }))
);
const AttendanceKioskPage = lazy(() =>
  import('../features/attendance/AttendanceKioskPage').then((module) => ({ default: module.AttendanceKioskPage }))
);
const RoutesPage = lazy(() =>
  import('../features/routes/RoutesPage').then((module) => ({ default: module.RoutesPage }))
);
const WorkTimePage = lazy(() =>
  import('../features/work-time/WorkTimePage').then((module) => ({ default: module.WorkTimePage }))
);
const FieldWorkPage = lazy(() =>
  import('../features/field-work/FieldWorkPage').then((module) => ({ default: module.FieldWorkPage }))
);

function tenantPage(pathname: string) {
  if (pathname === '/app') return <DashboardPage />;
  if (pathname === '/app/tasks') return <TasksPage />;
  if (pathname === '/app/employees') return <UsersPage />;
  if (pathname === '/app/partners') return <PartnersPage />;
  if (pathname === '/app/work-orders') return <WorkOrdersPage />;
  if (pathname === '/app/shifts') return <ShiftsPage />;
  if (pathname === '/app/attendance') return <AttendancePage />;
  if (pathname === '/app/work-time') return <WorkTimePage />;
  if (pathname === '/app/field-work') return <FieldWorkPage />;
  if (pathname === '/app/routes') return <RoutesPage />;
  if (pathname === '/app/leave') return <LeavePage />;
  if (pathname === '/app/documents') return <DocumentsPage />;
  if (pathname === '/app/notifications') return <NotificationsPage />;
  if (pathname === '/app/settings') return <SettingsPage />;
  return <Navigate to="/app" replace />;
}

function superadminPage(pathname: string) {
  if (pathname === '/superadmin' || pathname === '/superadmin/overview') {
    return <OverviewPage />;
  }
  if (pathname === '/superadmin/companies') return <CompaniesPage />;
  if (pathname === '/superadmin/modules') return <ModulesPage />;
  if (pathname === '/superadmin/infrastructure') return <InfrastructurePage />;
  if (pathname === '/superadmin/system') return <SystemPage />;
  return <Navigate to="/superadmin/overview" replace />;
}

export function App() {
  const { pathname } = useLocation();
  if (pathname === '/login') return <LoginPage />;
  if (pathname === '/activate') return <ActivatePage />;
  if (pathname === '/reset-password') return <ResetPasswordPage />;
  if (pathname === '/superadmin/login') return <SuperadminLoginPage />;

  if (pathname === '/kiosk/evidencija') {
    return <Suspense fallback={<div className="kiosk-loading"><span /><p>Pripremam QR kiosk…</p></div>}><AttendanceKioskPage /></Suspense>;
  }

  if (pathname === '/superadmin' || pathname.startsWith('/superadmin/')) {
    if (!readToken(superadminTokenKey)) {
      return <Navigate to="/superadmin/login" replace />;
    }
    return <SuperadminShell>{superadminPage(pathname)}</SuperadminShell>;
  }

  if (pathname === '/app' || pathname.startsWith('/app/')) {
    if (!readToken(tenantTokenKey)) return <Navigate to="/login" replace />;
    return <TenantShell><Suspense fallback={<div className="app-loading"><span /><p>Učitavam modul…</p></div>}>{tenantPage(pathname)}</Suspense></TenantShell>;
  }

  return <Navigate to="/app" replace />;
}
